import React, { useCallback, useEffect, useRef, useState } from 'react';
import './DesktopStrategizerPage.css';
import { useAuthContext } from '../../../context/AuthContext';
import useCompany from '../../../hooks/useCompany';
import { STRATEGIZER_BASE_URL, STRATEGIZER_ORIGIN } from '../../../config/strategizer';

const buildIframeSrc = (search, currentCompany) => {
  const params = new URLSearchParams(search || '');
  params.set('embed', '1');

  if (params.get('view') === 'dna') {
    const companyId = currentCompany?.id ||
      currentCompany?._id ||
      currentCompany?.company_id ||
      currentCompany?.company?.id ||
      currentCompany?.company?._id ||
      currentCompany?.company?.company_id;
    if (companyId && !params.get('companyId')) {
      params.set('companyId', companyId);
    }
  }

  return `${STRATEGIZER_BASE_URL}/?${params.toString()}`;
};

const DesktopStrategizerPage = ({ queryString = '' }) => {
  const { isAuthenticated } = useAuthContext();
  const { currentCompany } = useCompany();
  const iframeRef = useRef(null);
  const [iframeReady, setIframeReady] = useState(false);
  const ignoredQuerySyncRef = useRef(null);
  const allowIframeViewSyncRef = useRef(false);
  const [iframeSrc, setIframeSrc] = useState(() => {
    const initialSearch = typeof window !== 'undefined' ? window.location.search || '' : queryString;
    return buildIframeSrc(initialSearch, currentCompany);
  });

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== STRATEGIZER_ORIGIN) return;
      const payload = event.data;
      if (!payload || typeof payload !== 'object') return;
      if (payload.type === 'STRATEGIZER_READY' || payload.type === 'STRATEGIZER_REQUEST_AUTH') {
        allowIframeViewSyncRef.current = true;
        setIframeReady(true);
        return;
      }
      if (payload.type === 'STRATEGIZER_VIEW_CHANGE') {
        if (!allowIframeViewSyncRef.current) return;
        if (typeof window === 'undefined') return;
        const view = typeof payload.view === 'string' ? payload.view : '';
        const companyId = typeof payload.companyId === 'string' ? payload.companyId.trim() : '';
        const dnaId = typeof payload.dnaId === 'string' ? payload.dnaId.trim() : '';
        const params = new URLSearchParams(window.location.search || '');

        if (view === 'dna') {
          params.set('view', 'dna');
          if (companyId) params.set('companyId', companyId);
          else params.delete('companyId');
          if (dnaId) params.set('dnaId', dnaId);
          else params.delete('dnaId');
        } else {
          params.delete('view');
          params.delete('companyId');
          params.delete('dnaId');
        }

        const nextSearch = params.toString();
        const currentSearch = (window.location.search || '').replace(/^\?/, '');
        if (nextSearch === currentSearch) return;

        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
        window.history.replaceState(window.history.state || null, '', nextUrl);
        // Prevent iframe src recalculation only for this exact internal DNA<->board sync.
        ignoredQuerySyncRef.current = nextSearch ? `?${nextSearch}` : '';
        window.dispatchEvent(new Event('popstate'));
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isAuthenticated]);

  useEffect(() => {
    const normalizedQuery = queryString || '';
    if (ignoredQuerySyncRef.current !== null && normalizedQuery === ignoredQuerySyncRef.current) {
      ignoredQuerySyncRef.current = null;
      return;
    }
    setIframeSrc((prev) => {
      const next = buildIframeSrc(normalizedQuery, currentCompany);
      if (next !== prev) {
        // Wait for READY from the new iframe before accepting VIEW_CHANGE events.
        allowIframeViewSyncRef.current = false;
      }
      return next === prev ? prev : next;
    });
  }, [queryString, currentCompany]);

  return (
    <div className="desktop-strategizer-shell">
      <div className="desktop-strategizer-frame">
        <iframe
          ref={iframeRef}
          title="Strategizer"
          className="desktop-strategizer-iframe"
          src={iframeSrc}
          allow="clipboard-read; clipboard-write"
        />
      </div>
    </div>
  );
};

export default DesktopStrategizerPage;
